/**
 * MyRecoveryPackagesPage - Business User view of their recovery packages
 * ISO 22301 Clause 8.4 - Business Continuity Plans
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  FileText,
  Plus,
  Eye,
  Edit,
  RefreshCw,
  CheckCircle2,
  Clock,
  XCircle,
  AlertTriangle,
  Target,
  Zap,
  Timer,
  Snowflake,
  Wrench,
} from "lucide-react";

const STATUS_CONFIG = {
  draft: { label: "Draft", color: "bg-slate-100 text-slate-700", icon: Edit },
  submitted: { label: "Submitted", color: "bg-blue-100 text-blue-700", icon: Clock },
  under_review: { label: "Under Review", color: "bg-amber-100 text-amber-700", icon: Eye },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700", icon: XCircle },
  revision_required: { label: "Revision Required", color: "bg-sky-100 text-sky-700", icon: AlertTriangle },
};

const STRATEGY_CONFIG = {
  hot_standby: { label: "Hot Standby", color: "bg-red-100 text-red-700", icon: Zap },
  warm_standby: { label: "Warm Standby", color: "bg-amber-100 text-amber-700", icon: Timer },
  cold_standby: { label: "Cold Standby", color: "bg-blue-100 text-blue-700", icon: Snowflake },
  manual_workaround: { label: "Manual Workaround", color: "bg-slate-100 text-slate-700", icon: Wrench },
};

export default function MyRecoveryPackagesPage() {
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [packages, setPackages] = useState([]);
  const [availableBIAs, setAvailableBIAs] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch user's recovery packages
      const pkgResponse = await api.get("/recovery-packages/my-packages");
      setPackages(pkgResponse.data);
      
      // Fetch BIAs that can have recovery packages created
      try {
        const biaResponse = await api.get("/bia-engine/processes");
        const processes = biaResponse.data.processes || [];
        const biasWithoutPackages = processes.filter(
          bia => !pkgResponse.data.some(pkg => pkg.bia_id === bia.id)
        );
        setAvailableBIAs(biasWithoutPackages);
      } catch (e) {
        // BIA endpoint might have different structure
        setAvailableBIAs([]);
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
      toast.error("Failed to load recovery packages");
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <RefreshCw className="w-8 h-8 animate-spin text-sky-700" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-recovery-packages-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Badge className="bg-emerald-100 text-emerald-700">DO Phase</Badge>
            <Badge variant="outline">ISO 22301 Clause 8.4</Badge>
          </div>
          <h1 className="text-2xl font-bold text-slate-900">My Recovery Packages</h1>
          <p className="text-slate-600 mt-1">
            Create and manage recovery plans for your business processes
          </p>
        </div>
        <Button onClick={() => navigate("/operational-bia")} className="bg-sky-700 hover:bg-sky-800">
          <Plus className="w-4 h-4 mr-2" />
          Create from BIA
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Total Packages</p>
            <p className="text-2xl font-bold text-slate-900">{packages.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Draft</p>
            <p className="text-2xl font-bold text-slate-600">
              {packages.filter(p => p.status === "draft").length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Pending Review</p>
            <p className="text-2xl font-bold text-amber-600">
              {packages.filter(p => ["submitted", "under_review"].includes(p.status)).length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Approved</p>
            <p className="text-2xl font-bold text-emerald-600">
              {packages.filter(p => p.status === "approved").length}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-slate-500">Needs Revision</p>
            <p className="text-2xl font-bold text-sky-700">
              {packages.filter(p => p.status === "revision_required").length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Packages Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recovery Packages</CardTitle>
          <CardDescription>Click on a package to edit or view details</CardDescription>
        </CardHeader>
        <CardContent>
          {packages.length === 0 ? (
            <div className="text-center py-12">
              <Target className="w-12 h-12 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No Recovery Packages Yet</h3>
              <p className="text-slate-500 mb-4">
                Create your first recovery package by completing a BIA and clicking "Create Recovery Plan"
              </p>
              <Button onClick={() => navigate("/operational-bia")}>
                Go to BIA
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Process</TableHead>
                  <TableHead>Strategy</TableHead>
                  <TableHead>RTO (Target/Planned)</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {packages.map((pkg) => {
                  const status = STATUS_CONFIG[pkg.status] || STATUS_CONFIG.draft;
                  const strategy = STRATEGY_CONFIG[pkg.recovery_strategy?.strategy_type];
                  const StatusIcon = status.icon;
                  
                  return (
                    <TableRow key={pkg.id} className="cursor-pointer hover:bg-slate-50">
                      <TableCell>
                        <div>
                          <p className="font-medium">{pkg.process_name}</p>
                          <p className="text-xs text-slate-500">{pkg.department}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {strategy ? (
                          <Badge className={strategy.color}>
                            <strategy.icon className="w-3 h-3 mr-1" />
                            {strategy.label}
                          </Badge>
                        ) : (
                          <span className="text-slate-400">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span>{pkg.bia_summary?.rto_hours || "?"}</span>
                          <span className="text-slate-400">/</span>
                          <span className={
                            pkg.recovery_strategy?.planned_rto_hours > pkg.bia_summary?.rto_hours 
                              ? "text-red-600 font-medium" 
                              : "text-emerald-600 font-medium"
                          }>
                            {pkg.recovery_strategy?.planned_rto_hours || "?"} hrs
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={status.color}>
                          <StatusIcon className="w-3 h-3 mr-1" />
                          {status.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-500">
                          {new Date(pkg.updated_at).toLocaleDateString()}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {pkg.status === "draft" || pkg.status === "revision_required" ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/recovery-wizard/package/${pkg.id}`)}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/recovery-wizard/package/${pkg.id}`)}
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              View
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Review Comments (if any packages need revision) */}
      {packages.some(p => p.status === "revision_required" && p.review_comments) && (
        <Card className="border-sky-200 bg-sky-50">
          <CardHeader>
            <CardTitle className="text-sky-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Revision Required
            </CardTitle>
          </CardHeader>
          <CardContent>
            {packages.filter(p => p.status === "revision_required" && p.review_comments).map((pkg) => (
              <div key={pkg.id} className="mb-4 last:mb-0">
                <p className="font-medium text-sky-900">{pkg.process_name}</p>
                <p className="text-sm text-sky-800 mt-1">
                  <strong>BCM Manager Feedback:</strong> {pkg.review_comments}
                </p>
                <Button 
                  size="sm" 
                  className="mt-2"
                  onClick={() => navigate(`/recovery-wizard/package/${pkg.id}`)}
                >
                  Edit Package
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
